import React, { useState, useMemo } from 'react';
import {
  useReporterExamenVersDevoirsMutation,
  useDetecterDevoirsNonFaitsQuery,
  useNoteCollectiveMutation,
} from '../../services/notesDevoirsApi';
import { useGetMatieresQuery } from '../../services/matieresApi';
import { useGetClassesQuery, useGetParcoursListQuery } from '../../services/polesOptionsApi';
import { statistiquesService } from '../../services/api.service';
import {
  AlertTriangle, ArrowRight, CheckCircle2, Copy, Search,
  Users, Zap, RefreshCw, Printer, X, FileText, BarChart3, Loader, ArrowRightLeft,
} from 'lucide-react';
import apiClient from '../../config/api.config';
import ReportIntelligentModal from './ReportIntelligentModal';

interface Props {
  anneeAcademique: string;
}

const ActionsRapides: React.FC<Props> = ({ anneeAcademique }) => {
  const [semestre, setSemestre] = useState('S1');
  const [filtreNiveau, setFiltreNiveau] = useState('');
  const [filtrePole, setFiltrePole] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Exiger un filtre (niveau OU pôle) avant de lancer la détection
  // Sans filtre, le backend scanne toutes matières × classes (très lourd → plantage).
  const hasFilter = !!(filtreNiveau || filtrePole);

  // Données
  const { data: detection, isLoading: detecting, refetch: refetchDetection } = useDetecterDevoirsNonFaitsQuery(
    { annee_academique: anneeAcademique, semestre, niveau: filtreNiveau || undefined, pole: filtrePole || undefined },
    { skip: !anneeAcademique || !hasFilter }
  );
  const { data: matieres = [] } = useGetMatieresQuery();
  const { data: classes = [] } = useGetClassesQuery({ actif: true });

  // Limites d'affichage (évite de geler le navigateur si énormément de lignes)
  const MAX_ROWS_DISPLAY = 200;
  const nonFaitsAffiches = useMemo(
    () => (detection?.devoirs_non_faits || []).slice(0, MAX_ROWS_DISPLAY),
    [detection]
  );
  const partielsAffiches = useMemo(
    () => (detection?.devoirs_partiels || []).slice(0, MAX_ROWS_DISPLAY),
    [detection]
  );
  const [reporter, { isLoading: reporting }] = useReporterExamenVersDevoirsMutation();
  const [noteCollective, { isLoading: applyingCollective }] = useNoteCollectiveMutation();

  // État modal report intelligent (analyse + report tracé + undo)
  const [reportModalCtx, setReportModalCtx] = useState<null | {
    matiereId: number; matiereNom: string; matiereCode: string; classes: string[];
  }>(null);

  // État modal "Copier notes entre matières (même classe)"
  const [showCopier, setShowCopier] = useState(false);
  const [copierForm, setCopierForm] = useState({
    classe: '',
    matiere_source_id: '',
    matiere_cible_id: '',
    champ: 'les_deux' as 'note1' | 'note2' | 'les_deux',
    mode: 'manquants_seulement' as 'manquants_seulement' | 'tous',
    commentaire: '',
  });
  const [copierLoading, setCopierLoading] = useState(false);

  // État modal note collective
  const [showCollective, setShowCollective] = useState(false);
  const [collectiveForm, setCollectiveForm] = useState({
    matiere_id: '',
    classes: [] as string[],
    champ: 'les_deux',
    valeur: '',
    ecraser: false,
    scope: 'classes' as 'classes' | 'niveau' | 'parcours' | 'matiere',
    scopeNiveau: '',
    scopeParcours: '',
  });
  const [collPreview, setCollPreview] = useState<null | { nb_classes: number; nb_etudiants: number; classes: string[] }>(null);
  const { data: parcoursList = [] } = useGetParcoursListQuery({ actif: true });

  // Handler reporter examen
  const handleReporter = async (matiereId: number, classe: string, matiereNom: string) => {
    if (!window.confirm(
      `Reporter la note d'examen vers D1 et D2 pour :\n\n` +
      `Matière : ${matiereNom}\n` +
      `Classe : ${classe}\n\n` +
      `Les étudiants sans note de devoir recevront leur note d'examen en D1 et D2.\n` +
      `Les étudiants ayant déjà des notes ne seront pas modifiés.`
    )) return;
    try {
      const res = await reporter({ matiere_id: matiereId, annee_academique: anneeAcademique, semestre, classes: [classe] }).unwrap();
      setMessage({ type: 'success', text: res.message });
      refetchDetection();
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.data?.error || 'Erreur' });
    }
  };

  // Handler reporter tous les devoirs non faits avec examen
  const handleReporterTous = async () => {
    if (!detection) return;
    const avecExam = detection.devoirs_non_faits.filter(d => d.a_examen);
    if (avecExam.length === 0) return;
    if (!window.confirm(
      `Reporter la note d'examen pour ${avecExam.length} matière(s)/classe(s) sans devoir ?\n\n` +
      `Cela affectera les notes d'examen comme D1 et D2 pour tous les étudiants concernés.`
    )) return;

    let success = 0, errors = 0;
    for (const d of avecExam) {
      try {
        await reporter({ matiere_id: d.matiere_id, annee_academique: anneeAcademique, semestre, classes: [d.classe] }).unwrap();
        success++;
      } catch { errors++; }
    }
    setMessage({ type: success > 0 ? 'success' : 'error', text: `${success} traité(s), ${errors} erreur(s)` });
    refetchDetection();
  };

  // Preview du scope
  const handlePreviewScope = async () => {
    if (!collectiveForm.matiere_id) { setCollPreview(null); return; }
    try {
      const res = await statistiquesService.previewScopeNotes({
        scope: collectiveForm.scope,
        matiere_id: Number(collectiveForm.matiere_id),
        classes: collectiveForm.classes,
        niveau: collectiveForm.scopeNiveau || undefined,
        parcours_nom: collectiveForm.scopeParcours || undefined,
      });
      setCollPreview({ nb_classes: res.nb_classes, nb_etudiants: res.nb_etudiants, classes: res.classes });
    } catch {
      setCollPreview(null);
    }
  };

  // Handler note collective
  const handleNoteCollective = async () => {
    if (!collectiveForm.matiere_id || !collectiveForm.valeur) {
      setMessage({ type: 'error', text: 'Remplissez la matière et la valeur' }); return;
    }
    if (collectiveForm.scope === 'classes' && collectiveForm.classes.length === 0) {
      setMessage({ type: 'error', text: 'Sélectionnez au moins une classe' }); return;
    }
    if (collectiveForm.scope === 'parcours' && !collectiveForm.scopeParcours) {
      setMessage({ type: 'error', text: 'Choisissez un parcours' }); return;
    }
    try {
      const payload: any = {
        matiere_id: Number(collectiveForm.matiere_id),
        annee_academique: anneeAcademique,
        semestre,
        champ: collectiveForm.champ,
        valeur: Number(collectiveForm.valeur),
        ecraser_existants: collectiveForm.ecraser,
        scope: collectiveForm.scope,
      };
      if (collectiveForm.scope === 'classes') payload.classes = collectiveForm.classes;
      if (collectiveForm.scope === 'niveau') payload.niveau = collectiveForm.scopeNiveau;
      if (collectiveForm.scope === 'parcours') payload.parcours_nom = collectiveForm.scopeParcours;
      const res = await noteCollective(payload).unwrap();
      setMessage({ type: 'success', text: res.message });
      setShowCollective(false);
      setCollPreview(null);
      refetchDetection();
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.data?.error || 'Erreur' });
    }
  };

  // Handler copier notes entre matières
  const matieresPourClasse = useMemo(() => {
    if (!copierForm.classe) return [] as any[];
    return (matieres as any[]).filter(m => m.classe === copierForm.classe && m.actif !== false);
  }, [matieres, copierForm.classe]);

  const closeCopier = () => {
    setShowCopier(false);
    setCopierForm({
      classe: '', matiere_source_id: '', matiere_cible_id: '',
      champ: 'les_deux', mode: 'manquants_seulement', commentaire: '',
    });
  };

  const handleCopierNotes = async () => {
    if (!copierForm.classe || !copierForm.matiere_source_id || !copierForm.matiere_cible_id) {
      setMessage({ type: 'error', text: 'Choisis classe + matière source + matière cible' });
      return;
    }
    if (copierForm.matiere_source_id === copierForm.matiere_cible_id) {
      setMessage({ type: 'error', text: 'La matière source et la matière cible doivent être différentes' });
      return;
    }
    const sourceMat = matieresPourClasse.find(m => m.id === Number(copierForm.matiere_source_id));
    const cibleMat = matieresPourClasse.find(m => m.id === Number(copierForm.matiere_cible_id));
    const champLabel = copierForm.champ === 'les_deux' ? 'D1 + D2' : copierForm.champ === 'note1' ? 'D1' : 'D2';
    const modeLabel = copierForm.mode === 'tous'
      ? 'Mode : écrase les notes cibles existantes'
      : 'Mode : ne touche que les cases vides de la matière cible';
    if (!window.confirm(
      `Copier les notes ${champLabel} de :\n` +
      `  📘 SOURCE : ${sourceMat?.code_matiere || '?'} — ${sourceMat?.nom_matiere || '?'}\n` +
      `vers :\n` +
      `  📗 CIBLE : ${cibleMat?.code_matiere || '?'} — ${cibleMat?.nom_matiere || '?'}\n\n` +
      `Classe : ${copierForm.classe}\n${modeLabel}\n\n` +
      `→ Tracé avec batch_id (annulable via l'historique).`
    )) return;
    setCopierLoading(true);
    setMessage(null);
    try {
      const res = await apiClient.post('/notes-devoirs/copier-vers-matiere/', {
        matiere_source_id: Number(copierForm.matiere_source_id),
        matiere_cible_id: Number(copierForm.matiere_cible_id),
        classe: copierForm.classe,
        annee_academique: anneeAcademique,
        semestre,
        champ: copierForm.champ,
        mode: copierForm.mode,
        commentaire: copierForm.commentaire,
      });
      const r = res.data;
      const total = (r.nb_crees ?? 0) + (r.nb_modifies ?? 0);
      const skip: string[] = [];
      if (r.nb_cible_deja_saisie > 0) skip.push(`${r.nb_cible_deja_saisie} cibles déjà saisies`);
      if (r.nb_sans_source > 0) skip.push(`${r.nb_sans_source} sans note source`);
      if (r.nb_source_vide > 0) skip.push(`${r.nb_source_vide} source vide`);
      const skipMsg = skip.length > 0 ? ` · ${skip.join(', ')} ignoré(s)` : '';
      if (total > 0) {
        setMessage({
          type: 'success',
          text: `${total} note(s) copiée(s) ${champLabel} — ${r.nb_crees} créée(s), ${r.nb_modifies} modifiée(s)${skipMsg} · batch ${r.batch_id?.slice(0, 8)}…`,
        });
        closeCopier();
      } else {
        setMessage({
          type: 'error',
          text: `Aucune note copiée. ${skipMsg.replace('· ', '')}`.trim() || 'Aucune note copiée.',
        });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.error || 'Erreur lors de la copie' });
    } finally {
      setCopierLoading(false);
    }
  };

  // Niveaux et pôles pour filtres
  const niveaux = ['L1', 'L2', 'L3', 'M1', 'M2'];
  const poles = useMemo(() => {
    const s = new Set((classes as any[]).map((c: any) => c.pole_nom).filter(Boolean));
    return Array.from(s).sort();
  }, [classes]);

  // Recherche matière pour note collective
  const [matiereSearch, setMatiereSearch] = useState('');
  const matieresFiltrees = useMemo(() => {
    if (!matiereSearch || matiereSearch.length < 2) return [];
    const q = matiereSearch.toLowerCase();
    return (matieres as any[]).filter((m: any) =>
      m.nom_matiere?.toLowerCase().includes(q) || m.code_matiere?.toLowerCase().includes(q)
    ).slice(0, 15);
  }, [matieres, matiereSearch]);

  return (
    <div className="space-y-6">
      {/* Message */}
      {message && (
        <div className={`p-3 rounded-lg text-sm flex justify-between ${
          message.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200' : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200'
        }`}>
          {message.text}
          <button onClick={() => setMessage(null)}><X size={14} /></button>
        </div>
      )}

      {/* Filtres */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Semestre</label>
            <select value={semestre} onChange={e => setSemestre(e.target.value)} className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700">
              <option value="S1">S1</option><option value="S2">S2</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Niveau</label>
            <select value={filtreNiveau} onChange={e => setFiltreNiveau(e.target.value)} className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700">
              <option value="">Tous</option>
              {niveaux.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Pôle</label>
            <select value={filtrePole} onChange={e => setFiltrePole(e.target.value)} className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700">
              <option value="">Tous</option>
              {poles.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <button onClick={() => refetchDetection()} className="px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200">
            <RefreshCw size={16} className={detecting ? 'animate-spin' : ''} />
          </button>
          <button onClick={() => setShowCopier(true)}
            className="ml-auto flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white text-sm rounded-lg hover:bg-cyan-700"
            title="Copier les notes D1/D2 d'une matière vers une autre matière de la même classe">
            <ArrowRightLeft size={14} /> Copier notes (matière → matière)
          </button>
          <button onClick={() => setShowCollective(true)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700">
            <Copy size={14} /> Note collective
          </button>
        </div>
      </div>

      {/* Message si aucun filtre choisi */}
      {!hasFilter && (
        <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-sm text-blue-800 dark:text-blue-200">
          <strong>Choisissez au moins un niveau ou un pôle</strong> ci-dessus pour lancer la détection.
          <br />
          <span className="text-xs opacity-80">
            Sans filtre, l'analyse scanne toutes les matières × classes du système (plusieurs milliers de combinaisons) et peut ralentir fortement le navigateur.
          </span>
        </div>
      )}

      {/* Stats de détection */}
      {detection && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-red-50 dark:bg-red-900/15 border-2 border-red-200 dark:border-red-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-600 text-white rounded-lg"><AlertTriangle size={20} /></div>
              <div>
                <div className="text-2xl font-extrabold text-red-700">{detection.total_non_faits}</div>
                <div className="text-xs text-red-600">Devoirs non faits (0 note)</div>
              </div>
            </div>
          </div>
          <div className="bg-orange-50 dark:bg-orange-900/15 border-2 border-orange-200 dark:border-orange-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-600 text-white rounded-lg"><FileText size={20} /></div>
              <div>
                <div className="text-2xl font-extrabold text-orange-700">{detection.total_partiels}</div>
                <div className="text-xs text-orange-600">Saisie incomplète</div>
              </div>
            </div>
          </div>
          <div className="bg-green-50 dark:bg-green-900/15 border-2 border-green-200 dark:border-green-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-600 text-white rounded-lg"><Zap size={20} /></div>
              <div>
                <div className="text-2xl font-extrabold text-green-700">{detection.total_avec_examen}</div>
                <div className="text-xs text-green-600">Reportables (ont un examen)</div>
              </div>
            </div>
            {detection.total_avec_examen > 0 && (
              <button onClick={handleReporterTous} disabled={reporting}
                className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 disabled:opacity-50">
                <ArrowRight size={12} /> Reporter tous vers D1 & D2
              </button>
            )}
          </div>
        </div>
      )}

      {/* Liste des devoirs non faits */}
      {detection && detection.devoirs_non_faits.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-red-200 dark:border-red-800 overflow-hidden">
          <div className="px-4 py-3 bg-red-50 dark:bg-red-900/10 border-b border-red-200 dark:border-red-800">
            <h3 className="text-sm font-bold text-red-800 dark:text-red-200 flex items-center gap-2">
              <AlertTriangle size={16} /> Devoirs non faits ({detection.devoirs_non_faits.length})
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/50 text-xs text-gray-500 uppercase">
                <tr>
                  <th className="px-3 py-2 text-left">Matière</th>
                  <th className="px-3 py-2 text-left">Classe</th>
                  <th className="px-3 py-2 text-center">Étudiants</th>
                  <th className="px-3 py-2 text-center">Examen</th>
                  <th className="px-3 py-2 text-left">Suggestion</th>
                  <th className="px-3 py-2 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {nonFaitsAffiches.map((d, i) => (
                  <tr key={`${d.matiere_id}-${d.classe}`} className={i%2?'bg-gray-50 dark:bg-gray-800/60':''}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900 dark:text-gray-100 text-xs">{d.matiere_nom}</div>
                      <div className="text-[10px] text-gray-400">{d.matiere_code}</div>
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-xs font-bold">{d.classe}</span>
                      <span className="text-[10px] text-gray-400 ml-1">{d.pole}</span>
                    </td>
                    <td className="px-3 py-2 text-center text-xs font-bold">{d.nb_etudiants}</td>
                    <td className="px-3 py-2 text-center">
                      {d.a_examen ? (
                        <span className="px-2 py-0.5 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 rounded-full text-[10px] font-bold">Oui</span>
                      ) : (
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full text-[10px]">Non</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[10px] text-gray-500 italic">{d.suggestion}</td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex flex-col gap-1 items-stretch">
                        <button
                          onClick={() => setReportModalCtx({
                            matiereId: d.matiere_id, matiereNom: d.matiere_nom,
                            matiereCode: d.matiere_code, classes: [d.classe],
                          })}
                          className="px-2 py-1 bg-blue-600 text-white text-[10px] rounded hover:bg-blue-700 flex items-center justify-center gap-1"
                          title="Analyser puis reporter (avec undo)"
                        >
                          <BarChart3 size={10} /> Analyser & Reporter
                        </button>
                        {d.a_examen && (
                          <button
                            onClick={() => handleReporter(d.matiere_id, d.classe, d.matiere_nom)}
                            disabled={reporting}
                            className="px-2 py-1 bg-green-600 text-white text-[10px] rounded hover:bg-green-700 disabled:opacity-50"
                            title="Report direct (legacy, sans undo)"
                          >
                            Reporter exam
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {detection.devoirs_non_faits.length > MAX_ROWS_DISPLAY && (
              <div className="p-3 text-xs text-center text-gray-500 bg-gray-50 dark:bg-gray-900 border-t">
                Affichage limité à {MAX_ROWS_DISPLAY} lignes sur {detection.devoirs_non_faits.length}.
                Affinez les filtres pour voir les autres.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Liste des devoirs partiels */}
      {detection && detection.devoirs_partiels.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-orange-200 dark:border-orange-800 overflow-hidden">
          <div className="px-4 py-3 bg-orange-50 dark:bg-orange-900/10 border-b border-orange-200 dark:border-orange-800">
            <h3 className="text-sm font-bold text-orange-800 dark:text-orange-200 flex items-center gap-2">
              <FileText size={16} /> Saisie incomplète ({detection.devoirs_partiels.length})
            </h3>
          </div>
          <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/50 text-xs text-gray-500 uppercase sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">Matière</th>
                  <th className="px-3 py-2 text-left">Classe</th>
                  <th className="px-3 py-2 text-center">D1</th>
                  <th className="px-3 py-2 text-center">D2</th>
                  <th className="px-3 py-2 text-left">Suggestion</th>
                  <th className="px-3 py-2 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {partielsAffiches.map((d: any, i: number) => (
                  <tr key={`${d.matiere_id}-${d.classe}`} className={i%2?'bg-gray-50 dark:bg-gray-800/60':''}>
                    <td className="px-3 py-2 text-xs font-medium">{d.matiere_nom}</td>
                    <td className="px-3 py-2 text-xs font-bold">{d.classe}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-xs font-bold ${d.pct_d1 < 50 ? 'text-red-600' : d.pct_d1 < 80 ? 'text-orange-600' : 'text-green-600'}`}>{d.pct_d1}%</span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-xs font-bold ${d.pct_d2 < 50 ? 'text-red-600' : d.pct_d2 < 80 ? 'text-orange-600' : 'text-green-600'}`}>{d.pct_d2}%</span>
                    </td>
                    <td className="px-3 py-2 text-[10px] text-gray-500 italic">{d.suggestion}</td>
                    <td className="px-3 py-2 text-center">
                      <button
                        onClick={() => setReportModalCtx({
                          matiereId: d.matiere_id, matiereNom: d.matiere_nom,
                          matiereCode: d.matiere_code, classes: [d.classe],
                        })}
                        className="px-2 py-1 bg-blue-600 text-white text-[10px] rounded hover:bg-blue-700 flex items-center justify-center gap-1 mx-auto"
                      >
                        <BarChart3 size={10} /> Analyser
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {detection.devoirs_partiels.length > MAX_ROWS_DISPLAY && (
              <div className="p-3 text-xs text-center text-gray-500 bg-gray-50 dark:bg-gray-900 border-t">
                Affichage limité à {MAX_ROWS_DISPLAY} lignes sur {detection.devoirs_partiels.length}.
                Affinez les filtres pour voir les autres.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Aucun problème */}
      {detection && detection.devoirs_non_faits.length === 0 && detection.devoirs_partiels.length === 0 && (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-green-200 dark:border-green-800">
          <CheckCircle2 size={48} className="mx-auto text-green-400 mb-3" />
          <p className="text-lg font-medium text-green-700 dark:text-green-300">Tous les devoirs sont saisis</p>
          <p className="text-sm text-gray-500">Aucun devoir non fait détecté pour {semestre} — {anneeAcademique}</p>
        </div>
      )}

      {/* Modal Note Collective */}
      {showCollective && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="px-6 py-4 border-b border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/10">
              <h3 className="text-lg font-bold text-indigo-800 dark:text-indigo-200 flex items-center gap-2">
                <Copy size={20} /> Note académique collective
              </h3>
              <p className="text-xs text-indigo-600 dark:text-indigo-300 mt-0.5">
                Affecter une même note à tous les étudiants des classes sélectionnées
              </p>
            </div>
            <div className="p-6 space-y-4">
              {/* Matière */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Matière</label>
                <input type="text" value={matiereSearch}
                  onChange={e => { setMatiereSearch(e.target.value); }}
                  placeholder="Rechercher une matière..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700" />
                {matieresFiltrees.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border rounded-lg shadow-lg max-h-40 overflow-y-auto">
                    {matieresFiltrees.map((m: any) => (
                      <button key={m.id} type="button" onClick={() => {
                        setCollectiveForm(f => ({ ...f, matiere_id: String(m.id) }));
                        setMatiereSearch(`${m.code_matiere} — ${m.nom_matiere}`);
                      }} className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-blue-900/20">
                        <span className="font-mono text-xs">{m.code_matiere}</span> — {m.nom_matiere}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Portée de l'attribution */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Portée de l'attribution</label>
                <select value={collectiveForm.scope}
                  onChange={e => { setCollectiveForm(f => ({ ...f, scope: e.target.value as any })); setCollPreview(null); }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700">
                  <option value="classes">Classes sélectionnées</option>
                  <option value="niveau">Niveau entier (tous étudiants du niveau suivant la matière)</option>
                  <option value="parcours">Parcours complet (ex : Génie Mécanique)</option>
                  <option value="matiere">Matière complète (toutes les classes concernées)</option>
                </select>
              </div>

              {collectiveForm.scope === 'classes' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Classes ({collectiveForm.classes.length} sélectionnée{collectiveForm.classes.length > 1 ? 's' : ''})
                  </label>
                  <div className="flex flex-wrap gap-1.5 p-2 border border-gray-300 dark:border-gray-600 rounded-lg max-h-32 overflow-y-auto bg-white dark:bg-gray-700">
                    {(classes as any[]).map((c: any) => {
                      const sel = collectiveForm.classes.includes(c.code);
                      return (
                        <label key={c.id} className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs cursor-pointer select-none ${
                          sel ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border border-indigo-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-200'
                        }`}>
                          <input type="checkbox" checked={sel} onChange={e => {
                            setCollectiveForm(f => ({
                              ...f,
                              classes: e.target.checked ? [...f.classes, c.code] : f.classes.filter(x => x !== c.code),
                            }));
                            setCollPreview(null);
                          }} className="w-3 h-3" />
                          {c.code}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {collectiveForm.scope === 'niveau' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Niveau</label>
                  <select value={collectiveForm.scopeNiveau}
                    onChange={e => { setCollectiveForm(f => ({ ...f, scopeNiveau: e.target.value })); setCollPreview(null); }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700">
                    <option value="">— Choisir un niveau —</option>
                    {niveaux.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              )}

              {collectiveForm.scope === 'parcours' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Parcours</label>
                  <select value={collectiveForm.scopeParcours}
                    onChange={e => { setCollectiveForm(f => ({ ...f, scopeParcours: e.target.value })); setCollPreview(null); }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700">
                    <option value="">— Choisir un parcours —</option>
                    {(parcoursList as any[]).map((p: any) => (
                      <option key={p.id} value={p.nom}>{p.pole_nom} / {p.nom}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Preview périmètre */}
              <div className="flex items-center gap-2">
                <button type="button" onClick={handlePreviewScope}
                  disabled={!collectiveForm.matiere_id}
                  className="px-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 disabled:opacity-50">
                  Calculer le périmètre
                </button>
                {collPreview && (
                  <span className="text-xs text-gray-600 dark:text-gray-300">
                    <strong>{collPreview.nb_classes}</strong> classe(s) · <strong>{collPreview.nb_etudiants}</strong> étudiant(s)
                    {collPreview.classes.length > 0 && ` : ${collPreview.classes.slice(0, 5).join(', ')}${collPreview.classes.length > 5 ? ` +${collPreview.classes.length - 5}` : ''}`}
                  </span>
                )}
              </div>

              {/* Champ + Valeur */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Appliquer sur</label>
                  <select value={collectiveForm.champ} onChange={e => setCollectiveForm(f => ({ ...f, champ: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700">
                    <option value="les_deux">D1 et D2</option>
                    <option value="note1">D1 uniquement</option>
                    <option value="note2">D2 uniquement</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Note (/20)</label>
                  <input type="number" min="0" max="20" step="0.25"
                    value={collectiveForm.valeur}
                    onChange={e => setCollectiveForm(f => ({ ...f, valeur: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-lg font-bold text-center" />
                </div>
              </div>

              {/* Option écraser */}
              <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <input type="checkbox" checked={collectiveForm.ecraser}
                  onChange={e => setCollectiveForm(f => ({ ...f, ecraser: e.target.checked }))}
                  className="rounded" />
                Écraser les notes existantes (sinon ne remplit que les notes vides)
              </label>
            </div>

            <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button onClick={() => setShowCollective(false)}
                className="px-4 py-2 text-sm bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300">Annuler</button>
              <button onClick={handleNoteCollective} disabled={applyingCollective}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                {applyingCollective ? 'Application...' : 'Appliquer la note'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Report Intelligent (analyse + report tracé + undo) */}
      {reportModalCtx && (
        <ReportIntelligentModal
          open={!!reportModalCtx}
          onClose={() => { setReportModalCtx(null); refetchDetection(); }}
          matiereId={reportModalCtx.matiereId}
          matiereNom={reportModalCtx.matiereNom}
          matiereCode={reportModalCtx.matiereCode}
          classes={reportModalCtx.classes}
          anneeAcademique={anneeAcademique}
          semestre={semestre}
        />
      )}

      {/* Modal "Copier notes entre matières (même classe)" */}
      {showCopier && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col">
            {/* Header sticky */}
            <div className="flex items-center justify-between px-5 py-3 border-b dark:border-gray-700 shrink-0">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <ArrowRightLeft size={18} className="text-cyan-600" />
                Copier les notes d'une matière vers une autre
              </h3>
              <button onClick={closeCopier} className="text-gray-400 hover:text-gray-600">
                <X size={18} />
              </button>
            </div>

            {/* Body scrollable */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

            <div className="bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded p-2 text-xs text-cyan-800 dark:text-cyan-300">
              💡 Cas d'usage : un même formateur a fait une matière mais ses notes
              servent aussi pour une autre matière de la <strong>même classe</strong> (tronc commun).
              Toutes les modifications sont tracées et annulables.
            </div>

            {/* 1. Classe */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Classe <span className="text-red-500">*</span>
              </label>
              <select
                value={copierForm.classe}
                onChange={(e) => setCopierForm(f => ({ ...f, classe: e.target.value, matiere_source_id: '', matiere_cible_id: '' }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
              >
                <option value="">— Choisir la classe —</option>
                {(classes as any[]).filter(c => c.actif).map(c => (
                  <option key={c.code} value={c.code}>{c.code} — {c.option_nom}</option>
                ))}
              </select>
            </div>

            {/* 2. Matière source */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Matière SOURCE (notes à copier) <span className="text-red-500">*</span>
              </label>
              <select
                value={copierForm.matiere_source_id}
                onChange={(e) => setCopierForm(f => ({ ...f, matiere_source_id: e.target.value }))}
                disabled={!copierForm.classe}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 disabled:bg-gray-100 dark:disabled:bg-gray-900"
              >
                <option value="">— Choisir la matière source —</option>
                {matieresPourClasse.map((m: any) => (
                  <option key={m.id} value={m.id}>{m.code_matiere} — {m.nom_matiere}</option>
                ))}
              </select>
            </div>

            {/* 3. Matière cible */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Matière CIBLE (où coller les notes) <span className="text-red-500">*</span>
              </label>
              <select
                value={copierForm.matiere_cible_id}
                onChange={(e) => setCopierForm(f => ({ ...f, matiere_cible_id: e.target.value }))}
                disabled={!copierForm.classe}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 disabled:bg-gray-100 dark:disabled:bg-gray-900"
              >
                <option value="">— Choisir la matière cible —</option>
                {matieresPourClasse
                  .filter((m: any) => String(m.id) !== copierForm.matiere_source_id)
                  .map((m: any) => (
                    <option key={m.id} value={m.id}>{m.code_matiere} — {m.nom_matiere}</option>
                  ))}
              </select>
            </div>

            {/* 4. Champ */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Notes à copier</label>
              <select
                value={copierForm.champ}
                onChange={(e) => setCopierForm(f => ({ ...f, champ: e.target.value as any }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
              >
                <option value="les_deux">D1 et D2 (les deux notes)</option>
                <option value="note1">D1 uniquement</option>
                <option value="note2">D2 uniquement</option>
              </select>
            </div>

            {/* 5. Mode */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Mode</label>
              <div className="space-y-1.5">
                <label className="flex items-start gap-2 cursor-pointer p-2 border border-gray-200 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700/30">
                  <input
                    type="radio"
                    checked={copierForm.mode === 'manquants_seulement'}
                    onChange={() => setCopierForm(f => ({ ...f, mode: 'manquants_seulement' }))}
                    className="mt-0.5"
                  />
                  <div className="text-xs">
                    <div className="font-medium">Cases vides uniquement</div>
                    <div className="text-gray-500 dark:text-gray-400">
                      N'écrit que sur les cases vides de la matière cible.
                    </div>
                  </div>
                </label>
                <label className="flex items-start gap-2 cursor-pointer p-2 border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 rounded">
                  <input
                    type="radio"
                    checked={copierForm.mode === 'tous'}
                    onChange={() => setCopierForm(f => ({ ...f, mode: 'tous' }))}
                    className="mt-0.5"
                  />
                  <div className="text-xs">
                    <div className="font-medium text-amber-800 dark:text-amber-300">Écraser les notes cibles</div>
                    <div className="text-amber-700 dark:text-amber-400">
                      Remplace les notes existantes (anciennes valeurs tracées et annulables).
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* 6. Commentaire */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Commentaire (motif)</label>
              <input
                type="text"
                value={copierForm.commentaire}
                onChange={(e) => setCopierForm(f => ({ ...f, commentaire: e.target.value }))}
                placeholder="ex: tronc commun matières X et Y"
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
              />
            </div>

            </div>
            {/* Footer sticky */}
            <div className="flex justify-end gap-2 px-5 py-3 border-t dark:border-gray-700 bg-white dark:bg-gray-800 shrink-0">
              <button
                onClick={closeCopier}
                disabled={copierLoading}
                className="px-4 py-1.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-sm hover:bg-gray-300"
              >
                Annuler
              </button>
              <button
                onClick={handleCopierNotes}
                disabled={copierLoading || !copierForm.classe || !copierForm.matiere_source_id || !copierForm.matiere_cible_id}
                className="flex items-center gap-1 px-4 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {copierLoading ? <Loader size={14} className="animate-spin" /> : <ArrowRightLeft size={14} />}
                Copier les notes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActionsRapides;
